import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Film, Pencil, Play, Trash2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { formatDate } from 'utils/formatDate';
import type { TutorialVideo } from 'api/tutorialsApi';
import { megabytes } from './utils';

interface TutorialRowProps {
  video: TutorialVideo;
  canManage: boolean;
  onPlay: (id: string) => void;
  onEdit: (video: TutorialVideo) => void;
  onDelete: (video: TutorialVideo) => void;
}

const TutorialRow = ({ video, canManage, onPlay, onEdit, onDelete }: TutorialRowProps) => {
  const { t, i18n } = useTranslation();
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const { id, title, description, url, downloadUrl, thumbnailUrl, createdAt, size } = video;
  const preview = thumbnailUrl ?? url;

  return (
    <li className="relative flex items-center gap-3 p-3 transition-colors first:rounded-t-lg last:rounded-b-lg hover:bg-muted/40">
      <div className="relative flex aspect-video w-28 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted sm:w-40">
        {failedUrl === preview ? (
          <Film className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
        ) : (
          <>
            {thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt=""
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
                onError={() => setFailedUrl(preview)}
              />
            ) : (
              <video
                // No JPEG stored: the video's own frame stands in
                src={`${url}#t=1`}
                preload="metadata"
                muted
                playsInline
                tabIndex={-1}
                aria-hidden="true"
                className="absolute inset-0 h-full w-full object-cover"
                onError={() => setFailedUrl(preview)}
              />
            )}
            <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white">
              <Play className="h-4 w-4 fill-current" aria-hidden="true" />
            </span>
          </>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <button
          type="button"
          aria-label={t('Play {{title}}', { title })}
          onClick={() => onPlay(id)}
          className="block w-full break-words text-left text-sm font-medium after:absolute after:inset-0 focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-ring"
        >
          {title}
        </button>
        {description && <p className="mt-0.5 line-clamp-2 break-words text-[13px] text-muted-foreground">{description}</p>}
        <p className="mt-1 text-xs text-muted-foreground">
          {formatDate(createdAt)} · {megabytes(size, i18n.language)}
        </p>
      </div>
      <div className="relative z-10 flex shrink-0 items-center gap-1">
        <Button asChild variant="ghost" size="icon">
          <a href={downloadUrl} target="_blank" rel="noopener noreferrer" aria-label={t('Download {{title}}', { title })}>
            <Download aria-hidden="true" />
          </a>
        </Button>
        {canManage && (
          <>
            <Button variant="ghost" size="icon" aria-label={t('Edit {{title}}', { title })} onClick={() => onEdit(video)}>
              <Pencil aria-hidden="true" />
            </Button>
            <Button variant="ghost" size="icon" aria-label={t('Delete {{title}}', { title })} onClick={() => onDelete(video)}>
              <Trash2 aria-hidden="true" />
            </Button>
          </>
        )}
      </div>
    </li>
  );
};

export default TutorialRow;
